import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import { getIssuanceDetailApi, acceptIssuanceApi, rejectIssuanceApi, reviewRejectionApi } from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, getIssuanceStatusLabel, rejectionReasonOptions } from '../../constants';
import FilterSelect from 'components/Form/filterSelect';
import { Modal, Switch, Radio, Alert } from 'antd';
import { useUniformMgmt } from '../../context/uniformMgmtContext';

const ISSUANCE_LIST_PATH = '/uniform-management/issuance';

// This page is reached from five places (issuance list, batch card, rejection
// review, an employee's profile, My Uniforms), so a fixed "back to list" link
// would strand four of them. React Router stacks an `idx` on history.state;
// when it is above zero there is a real previous page to return to, and only a
// direct URL open (idx 0) falls through to the list.
const useBackTarget = () => {
  const navigate = useNavigate();
  const canGoBack = typeof window !== 'undefined' && (window.history.state?.idx ?? 0) > 0;
  return {
    label: canGoBack ? 'Back' : 'Issuances',
    onClick: canGoBack ? () => navigate(-1) : undefined,
    to: canGoBack ? undefined : ISSUANCE_LIST_PATH,
  };
};

// The rejection review lifecycle reuses the issuance status palette rather
// than defining a fourth set of colours: pending = amber, approved = green,
// denied = red.
const reviewBadgeStatus = {
  PENDING: 'PENDING',
  APPROVED: 'ACCEPTED',
  DENIED: 'REJECTED',
};
const reviewLabels = { PENDING: 'Pending review', APPROVED: 'Approved', DENIED: 'Denied' };

const Meta = ({ label, wide, children }) => (
  <div className={wide ? 'detail-meta-item detail-meta-item--wide' : 'detail-meta-item'}>
    <div className="detail-meta-label">{label}</div>
    <div className="detail-meta-value">{children || <span className="text-muted">&mdash;</span>}</div>
  </div>
);

const thumbStyle = {
  width: 96,
  height: 96,
  flex: '0 0 96px',
  borderRadius: 12,
  background: '#F9FAFB',
  border: '1px solid #F1F2F4',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const IssuanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const back = useBackTarget();

  const { warehouseOptions, warehousesLoading } = useUniformMgmt();

  const [issuance, setIssuance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState(null);
  const [rejectDetail, setRejectDetail] = useState('');

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState('APPROVED');
  const [returnWarehouse, setReturnWarehouse] = useState(null);
  const [creditStock, setCreditStock] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);

  const fetchData = () => {
    setLoading(true);
    getIssuanceDetailApi(id).then(response => {
      setIssuance(response.data?.data || null);
      setLoading(false);
    }).catch(() => { setLoading(false); navigate('/uniform-management/issuance'); });
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleAccept = () => {
    setActionLoading(true);
    acceptIssuanceApi(id).then(() => {
      toast.success('Issuance accepted', { position: "top-right", autoClose: 3000, transition: Bounce });
      setActionLoading(false);
      fetchData();
    }).catch(e => {
      setActionLoading(false);
      toast.error(e.response?.data?.detail || 'Failed to accept', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const openReview = () => {
    setReviewAction('APPROVED');
    setReturnWarehouse(null);
    setCreditStock(true);
    setShowReviewModal(true);
  };

  const handleReview = () => {
    const rejectionId = issuance?.rejection?.id;
    if (!rejectionId) {
      toast.error('No rejection record found', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setReviewLoading(true);
    reviewRejectionApi(rejectionId, {
      review_status: reviewAction,
      return_to_warehouse: reviewAction === 'APPROVED' && returnWarehouse ? returnWarehouse.value : null,
      credit_stock: reviewAction === 'APPROVED' ? creditStock : true,
    }).then(() => {
      toast.success(`Rejection ${reviewAction.toLowerCase()}`, { position: "top-right", autoClose: 3000, transition: Bounce });
      setReviewLoading(false);
      setShowReviewModal(false);
      fetchData();
    }).catch(e => {
      setReviewLoading(false);
      const data = e.response?.data;
      const msg = data?.error || data?.detail || data?.non_field_errors?.[0] || 'Review failed';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const handleReject = () => {
    if (!rejectReason) {
      toast.error('Please select a reason', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setActionLoading(true);
    rejectIssuanceApi(id, {
      reason: rejectReason.value,
      reason_detail: rejectDetail
    }).then(() => {
      toast.success('Issuance rejected', { position: "top-right", autoClose: 3000, transition: Bounce });
      setActionLoading(false);
      setShowRejectModal(false);
      fetchData();
    }).catch(e => {
      setActionLoading(false);
      toast.error(e.response?.data?.detail || 'Failed to reject', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };


  if (loading || !issuance) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const rejection = issuance.rejection;
  const reviewPending = rejection && (!rejection.review_status || rejection.review_status === 'PENDING');

  /** One sentence on what HR did with the rejection and what followed.
   *  Without it the page reads as contradicting itself: denying a rejection
   *  puts the issuance back to Accepted by design, so the status at the top
   *  and the review status on the section look like they disagree. */
  const reviewOutcome = (() => {
    if (!rejection) return null;
    const who = rejection.reviewed_by_name || 'HR';
    const when = rejection.reviewed_at ? ` on ${formatDateHandler(rejection.reviewed_at)}` : '';
    if (reviewPending) {
      return {
        tone: '#B54708', icon: 'fa-clock-o',
        text: 'Awaiting HR review. The issuance stays Rejected until HR decides.',
      };
    }
    if (rejection.review_status === 'DENIED') {
      return {
        tone: '#1D4ED8', icon: 'fa-info-circle',
        text: `${who} denied the rejection${when}. The issuance stands as Accepted and the employee keeps the uniform.`,
      };
    }
    const stock = rejection.credit_stock === false
      ? 'Stock was NOT credited back (damaged or lost).'
      : `Stock was credited back to ${rejection.return_to_warehouse_name || 'the issuing warehouse'}.`;
    return {
      tone: '#067647', icon: 'fa-check-circle',
      text: `${who} approved the rejection${when}. ${stock}`,
    };
  })();
  const sizeLabel = issuance.size_detail
    ? `${issuance.size_detail.name} (${issuance.size_detail.code})`
    : null;

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">

        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <h3 className="detail-title">Issuance #{issuance.id}</h3>
              <span className="detail-status" style={getStatusBadge(issuance.status)}>
                {getIssuanceStatusLabel(issuance.status)}
              </span>
              {/* Why an issuance carrying a rejection reads Accepted (denied)
                  or Rejected (approved): the verdict, beside the status it
                  produced, rather than only further down the page. */}
              {rejection && !reviewPending && (
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Rejection {(reviewLabels[rejection.review_status] || rejection.review_status).toLowerCase()} by {rejection.reviewed_by_name || 'HR'}
                  {rejection.reviewed_at ? ` on ${formatDateHandler(rejection.reviewed_at)}` : ''}
                </span>
              )}
            </div>
            {/* Back link sits under the title, as on the document and asset detail pages. */}
            {back.to ? (
              <Link to={back.to} className="detail-back mt-1">
                <i className="fa fa-arrow-left" aria-hidden="true" />
                {back.label}
              </Link>
            ) : (
              <button type="button" className="detail-back mt-1" onClick={back.onClick}>
                <i className="fa fa-arrow-left" aria-hidden="true" />
                {back.label}
              </button>
            )}
          </div>
          {/* Accept/Reject apply only while ISSUED; Review only while a
              rejection is awaiting a decision. They never show together, so
              the header carries at most one primary action. */}
          <div className="detail-actions d-flex align-items-center flex-wrap gap-2">
            {issuance.status === 'ISSUED' && (
              <>
                <Button label="Reject" icon="fa fa-times" className="btn btn-outline-secondary btn-sm"
                  loading={actionLoading} disabled={actionLoading} onClick={() => setShowRejectModal(true)} />
                <Button label="Accept" icon="fa fa-check" className="btn btn-primary btn-sm"
                  loading={actionLoading} disabled={actionLoading} onClick={handleAccept} />
              </>
            )}
            {reviewPending && (
              <Button label="Review Rejection" icon="fa fa-gavel" className="btn btn-primary btn-sm"
                onClick={openReview} />
            )}
          </div>
        </div>

        <div className="detail-shell">
          <section className="detail-section">
            <div className="detail-section-head">
              <h2 className="detail-section-title">Issuance</h2>
            </div>
            <div className="detail-meta">
              <Meta label="Issuance Date">{formatDateHandler(issuance.issuance_date)}</Meta>
              <Meta label="Next Replacement">
                {issuance.next_replacement_date ? formatDateHandler(issuance.next_replacement_date) : null}
              </Meta>
              <Meta label="Warehouse">{issuance.warehouse_name}</Meta>
              <Meta label="Issued By">{issuance.issued_by_name}</Meta>
              <Meta label="Batch Reference">
                {issuance.batch_reference ? (
                  issuance.batch ? (
                    <Link to={`/uniform-management/issuance/batches/${issuance.batch}`} className="table-link">
                      {issuance.batch_reference}
                    </Link>
                  ) : issuance.batch_reference
                ) : null}
              </Meta>
              {issuance.notes ? (
                <Meta label="Notes" wide><span style={{ whiteSpace: 'pre-wrap' }}>{issuance.notes}</span></Meta>
              ) : null}
            </div>
          </section>

          <section className="detail-section">
            <div className="detail-section-head">
              <h2 className="detail-section-title">Employee</h2>
            </div>
            <div className="detail-meta">
              <Meta label="Employee">
                {issuance.company_user ? (
                  <Link to={`/employees/${issuance.company_user}`} className="table-link">
                    {issuance.company_user_name}
                  </Link>
                ) : issuance.company_user_name}
                {issuance.company_user_emp_no && (
                  <div className="text-muted fw-normal" style={{ fontSize: 12 }}>{issuance.company_user_emp_no}</div>
                )}
              </Meta>
              <Meta label="Cost Center">{issuance.casa_name}</Meta>
              <Meta label="Position">{issuance.position_name}</Meta>
            </div>
          </section>

          <section className="detail-section">
            <div className="detail-section-head">
              <h2 className="detail-section-title">Item</h2>
            </div>
            <div className="d-flex gap-3 align-items-start">
              <div style={thumbStyle}>
                {issuance.uniform_item_image ? (
                  <img src={issuance.uniform_item_image} alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <i className="fa fa-tshirt" aria-hidden="true" style={{ fontSize: 32, color: '#9CA3AF' }} />
                )}
              </div>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-text)', marginBottom: 10 }}>
                  {issuance.uniform_item_name}
                </div>
                <div className="detail-meta">
                  <Meta label="Size">{sizeLabel}</Meta>
                  <Meta label="Quantity"><span className="detail-num">{issuance.quantity}</span></Meta>
                </div>
              </div>
            </div>
          </section>

          {rejection && (
            <section className="detail-section" style={{ background: '#FEF2F2', borderTopColor: '#FECACA' }}>
              <div className="detail-section-head">
                <h2 className="detail-section-title d-flex align-items-center gap-2" style={{ color: '#B91C1C' }}>
                  <i className="fa fa-exclamation-triangle" aria-hidden="true" /> Rejection
                </h2>
                {rejection.review_status && (
                  <span className="detail-status" style={getStatusBadge(reviewBadgeStatus[rejection.review_status] || 'PENDING')}>
                    {reviewLabels[rejection.review_status] || rejection.review_status}
                  </span>
                )}
              </div>
              {reviewOutcome && (
                <div className="mb-3 p-2" style={{
                  background: '#FFFFFF', borderRadius: 8, fontSize: 13,
                  color: reviewOutcome.tone, fontWeight: 500,
                }}>
                  <i className={`fa ${reviewOutcome.icon} me-2`} aria-hidden="true" />
                  {reviewOutcome.text}
                </div>
              )}
              <div className="detail-meta">
                <Meta label="Reason">
                  {rejectionReasonOptions.find(r => r.value === rejection.reason)?.label || rejection.reason}
                </Meta>
                <Meta label="Rejected At">{formatDateHandler(rejection.rejected_at)}</Meta>
                {rejection.reviewed_by && (
                  <Meta label="Reviewed By">
                    <div>{rejection.reviewed_by_name || `User #${rejection.reviewed_by}`}</div>
                    {rejection.reviewed_by_emp_no && (
                      <div className="text-muted fw-normal" style={{ fontSize: 12 }}>{rejection.reviewed_by_emp_no}</div>
                    )}
                  </Meta>
                )}
                {rejection.reviewed_at && (
                  <Meta label="Reviewed At">{formatDateHandler(rejection.reviewed_at)}</Meta>
                )}
                {rejection.review_status === 'APPROVED' && (
                  // Recorded on approval and, until now, visible nowhere but
                  // the Django admin.
                  <Meta label="Stock credited back?">
                    {rejection.credit_stock === false
                      ? 'No — damaged or lost'
                      : `Yes — to ${rejection.return_to_warehouse_name || 'the issuing warehouse'}`}
                  </Meta>
                )}
                {rejection.reason_detail ? (
                  <Meta label="Detail" wide><span style={{ whiteSpace: 'pre-wrap' }}>{rejection.reason_detail}</span></Meta>
                ) : null}
              </div>
            </section>
          )}
        </div>

        {/* Reject Modal */}
        <Modal
          title="Reject Issuance"
          open={showRejectModal}
          onCancel={() => setShowRejectModal(false)}
          width={460}
          centered
          footer={[
            <button key="cancel" type="button" className="btn btn-light"
              onClick={() => setShowRejectModal(false)} disabled={actionLoading}>
              Cancel
            </button>,
            <Button key="submit" label="Reject" className="btn btn-danger ms-2"
              loading={actionLoading} onClick={handleReject} />
          ]}
        >
          <div className="mb-3">
            <label className="col-form-label">Reason <span className="text-danger">*</span></label>
            <FilterSelect isClearable onChange={setRejectReason} optionLabel="label" optionValue="value"
              options={rejectionReasonOptions} value={rejectReason} placeholder="Select Reason" isLoading={false} />
          </div>
          <div className="mb-1">
            <label className="col-form-label">Detail <small className="text-muted">(optional)</small></label>
            <textarea className="form-control" rows={3} value={rejectDetail}
              onChange={(e) => setRejectDetail(e.target.value)} placeholder="Additional details..." />
          </div>
        </Modal>

        {/* Review Rejection Modal */}
        <Modal
          title="Review Rejection"
          open={showReviewModal}
          onCancel={() => setShowReviewModal(false)}
          width={520}
          centered
          destroyOnHidden
          footer={[
            <button key="cancel" type="button" className="btn btn-light"
              onClick={() => setShowReviewModal(false)} disabled={reviewLoading}>
              Cancel
            </button>,
            <Button
              key="submit"
              label={reviewAction === 'APPROVED' ? 'Approve Rejection' : 'Deny Rejection'}
              className={`btn ${reviewAction === 'APPROVED' ? 'btn-success' : 'btn-danger'} ms-2`}
              loading={reviewLoading}
              onClick={handleReview}
            />
          ]}
        >
          <div>
            <div className="p-3 mb-3" style={{
              background: '#F9FAFB', borderRadius: 10, border: '1px solid #F1F2F4'
            }}>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted" style={{ fontSize: 12 }}>Employee</span>
                <span className="fw-semibold">{issuance.company_user_name}</span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted" style={{ fontSize: 12 }}>Uniform</span>
                <span className="fw-semibold">{issuance.uniform_item_name}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted" style={{ fontSize: 12 }}>Size</span>
                <span className="fw-semibold">{issuance.size_detail?.code || '—'}</span>
              </div>
            </div>

            <div className="mb-3">
              <label className="col-form-label">Decision</label>
              <Radio.Group
                value={reviewAction}
                onChange={(e) => setReviewAction(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                style={{ width: '100%' }}
              >
                <Radio.Button value="APPROVED" style={{ width: '50%', textAlign: 'center' }}>
                  <i className="fa fa-check me-1" />Approve
                </Radio.Button>
                <Radio.Button value="DENIED" style={{ width: '50%', textAlign: 'center' }}>
                  <i className="fa fa-times me-1" />Deny
                </Radio.Button>
              </Radio.Group>
            </div>

            {reviewAction === 'APPROVED' && (
              <>
                <div className="mb-3 d-flex align-items-center justify-content-between p-3"
                  style={{ background: '#F9FAFB', borderRadius: 10 }}>
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 14 }}>Credit stock back?</div>
                    <small className="text-muted">
                      {creditStock ? 'Stock will be returned to warehouse' : 'Item is damaged or lost'}
                    </small>
                  </div>
                  <Switch checked={creditStock} onChange={setCreditStock} />
                </div>
                {creditStock && (
                  <div className="mb-2">
                    <label className="col-form-label">
                      Return to Warehouse <small className="text-muted">(optional override)</small>
                    </label>
                    <FilterSelect isClearable onChange={setReturnWarehouse} optionLabel="label" optionValue="value"
                      options={warehouseOptions} value={returnWarehouse}
                      placeholder="Use original warehouse" isLoading={warehousesLoading} />
                  </div>
                )}
              </>
            )}

            {reviewAction === 'DENIED' && (
              <Alert
                type="warning"
                showIcon
                message="The issuance status will revert to Accepted and the employee will be notified."
                style={{ borderRadius: 10 }}
              />
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default IssuanceDetail;
